import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api-context';
import { queryKeys } from '../query-keys';
import {
  userListSchema,
  userResultSchema,
  type CreateUserInput,
  type Me,
  type UpdateUserInput,
  type UserList,
  type UserResult,
} from '../schemas';
import { useToken } from './use-token';

/**
 * Membres du cabinet de l'utilisateur connecté. Le cabinet vient du jeton :
 * aucun paramètre à passer, aucun cabinet voisin à atteindre.
 */
export function useCabinetUsers() {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.users.list(),
    queryFn: () => api.get<UserList>('/users', { schema: userListSchema }),
    enabled: token !== null,
  });
}

/**
 * Crée un accès pour une personne du même cabinet. Le compte est créé en
 * `membre` : un compte secondaire, sans les options du cabinet. Réservé aux
 * admins du cabinet, l'API répond 403 sinon.
 */
export function useCreateCabinetUser() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateUserInput) =>
      api.post<UserResult>('/users', input, { schema: userResultSchema }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.users.all() }),
  });
}

/** Promotion en admin du cabinet, ou activation/désactivation d'un accès. */
export function useUpdateCabinetUser() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateUserInput & { id: string }) =>
      api.patch<UserResult>(`/users/${id}`, input, { schema: userResultSchema }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.users.all() }),
  });
}

export function useDeleteCabinetUser() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/users/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.users.all() }),
  });
}

/**
 * Modifie ses propres informations (hors adresse email, qui est l'identifiant
 * de connexion). La fiche mise en cache est remplacée par la réponse : le nom
 * affiché dans la coque change sans recharger la session.
 */
export function useUpdateOwnProfile() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      input: Pick<UpdateUserInput, 'prenom' | 'nom' | 'indicatif' | 'telephone' | 'fonction'>,
    ) => api.patch<UserResult>('/users/me', input, { schema: userResultSchema }),
    onSuccess: ({ user }) => {
      queryClient.setQueryData<Me>(queryKeys.auth.me(), (me) => (me ? { ...me, user } : me));
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
    },
  });
}

/**
 * Supprime son propre compte, après avoir tapé l'adresse. Le jeton ne vaut plus
 * rien dès que la réponse arrive : il est jeté ici, comme à la déconnexion.
 * Le compte responsable emporte le cabinet avec lui, mais seulement s'il n'a ni
 * abonnement ni autre utilisateur : sinon l'API répond 403 et il faut passer
 * par le support.
 */
export function useDeleteOwnAccount() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) =>
      api.post<{ cabinetSupprime: boolean }>('/users/me/suppression', { email }),
    onSuccess: () => {
      api.tokens.clear();
      queryClient.clear();
    },
  });
}
